/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  observable,
  freeze,
}                                              from 'rewire-core';
import {
  ITimecardTest,
}                                              from '../DataTypes';
import DataMutations                           from '../graphql/Mutations';
import DataQueries                             from '../graphql/Queries';
import { notifications }                       from '../ui/Notifications';
import timecardTestExpectedResultsModel        from './TimecardTestExpectedResultsModel';
import timecardTestSummaryDailyRollupModel     from './TimecardTestSummaryDailyRollupModel';

interface ITimecardTestSummaryState {
  test?:     ITimecardTest;
  isLoading: boolean;
}

export class TimecardTestSummaryModel {
  state: ITimecardTestSummaryState;
 
  constructor() {
    this.state = observable({
      test: undefined,
      isLoading: false,
    });
  }

  reset() {
    freeze(() => {
      this.test      = undefined;
      this.isLoading = false;
    });
  }

  async load(testId?: string) {
    this.reset();
    this.isLoading = true;
    try {
      if (testId) {
        this.test = await this.getTimecardTestData(testId);
      }
      timecardTestExpectedResultsModel.load(this.test);
      timecardTestSummaryDailyRollupModel.load(this.test);
    } finally {
      this.isLoading = false;
    }
  }

  get test(): ITimecardTest | undefined {
    return this.state.test;
  }

  set test(value: ITimecardTest | undefined) {
    this.state.test = value;
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  set isLoading(value: boolean) {
    this.state.isLoading = value;
  }

  get hasChanges(): boolean {
    return timecardTestExpectedResultsModel.hasChanges();
  }

  private async getTimecardTestData(testId: string): Promise<ITimecardTest | undefined> {
    const returnSchema = `
      id
      code
      name
      employee {
        id
        name
      }
      effective
      total
      difference
      status
      lastRan
      lastEditedBy
      tags
      originalTimecards {
        id
        earnedDate
        effective
        accountCode
        units
        factor
        rate
        amount
        reason {
          id
          name
          category
        }
        occupation {
          id
          name
        }
        group {
          id
          name
        }
        template {
          id
          name
          code
        }
      }
      actualTimecards {
        id
        earnedDate
        effective
        accountCode
        units
        factor
        rate
        amount
        reason {
          id
          name
          category
        }
        occupation {
          id
          name
        }
        group {
          id
          name
        }
        template {
          id
          name
          code
        }
      }
      details {
        units
        rate
        factor
        amount
        difference
        status
        reason {
          id
          name
          category
        }
        overtimeReason {
          id
          name
        }
      }
    `;
    const result = await DataQueries.getTimecardTestsByIds(returnSchema, [testId]);
    return result?.[0] as ITimecardTest | undefined;
  }

  cancel() {
    timecardTestExpectedResultsModel.cancel();
  }

  canSave(): boolean {
    return true;
  }

  save = async () => {
    if (!this.test || !this.canSave() || !timecardTestExpectedResultsModel.save()) return;

    const testUpdateInput = {
      id:   this.test.id,
      name: timecardTestExpectedResultsModel.form.field.name.value,
      code: this.test.code,
      tags: timecardTestExpectedResultsModel.form.field.tags.value,
    };
    try {
      const results = await DataMutations.updateTimecardTests('', [testUpdateInput]);
      if (results) {
        notifications.success('Timecard Test changes saved');
      } else {
        notifications.error('Timecard Test save failed');
      }
    }
    catch (ex) {}
  };
}

const timecardTestSummaryModel = new TimecardTestSummaryModel();
export default timecardTestSummaryModel;